/**
 * EditPhotoLinkPanel — admin fixes a set whose photographer sent the wrong
 * Drive link. Saves the new "Link ảnh" and the worker re-queues the preview
 * sync, so the card shows the right photos after the next cron tick (~1 min).
 *
 * If the set was already imported into the admin's Drive, that copy came from
 * the old link — offer to blank the admin link so it can be re-imported.
 */

import React, { useState } from "react";
import { extractDriveFolderId, type PhotoRecord } from "@kra/photo-journal-shared";
import { patchPhoto } from "../lib/api.js";
import { cn } from "../lib/utils.js";

interface EditPhotoLinkPanelProps {
  photo: PhotoRecord;
  onClose: () => void;
  onSaved: (patch: Partial<PhotoRecord>) => void;
}

export function EditPhotoLinkPanel({ photo, onClose, onSaved }: EditPhotoLinkPanelProps) {
  const hasAdminLink = Boolean(photo.adminDriveLink?.trim());
  const [link, setLink] = useState("");
  const [clearAdmin, setClearAdmin] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setLink(text.trim());
    } catch {
      /* clipboard blocked — admin pastes into the field by hand */
    }
  };

  const handleSave = async () => {
    const next = link.trim();
    const folderId = extractDriveFolderId(next);
    if (!folderId) {
      setError("Link phải là folder Google Drive (…/folders/…)");
      return;
    }
    if (folderId === extractDriveFolderId(photo.driveLink ?? "")) {
      setError("Link này trùng link cũ");
      return;
    }

    const clearAdminImport = hasAdminLink && clearAdmin;
    setSaving(true);
    setError(null);
    try {
      await patchPhoto(photo.id, { driveLink: next, clearAdminImport });
      onSaved({
        driveLink: next,
        previewSync: "Queued",
        ...(clearAdminImport ? { adminDriveLink: "", adminCategoryFolder: "" } : {}),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lưu không được — thử lại");
      setSaving(false);
    }
  };

  return (
    <section className="mb-6 rounded-[24px] border border-surface-2 bg-white/80 px-4 py-4 md:px-5">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-3">
        Sửa link ảnh
      </p>

      <div className="flex gap-2">
        <input
          type="url"
          inputMode="url"
          autoFocus
          value={link}
          onChange={(e) => {
            setLink(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleSave();
          }}
          placeholder="Dán link Google Drive đúng…"
          className="min-w-0 flex-1 rounded-[14px] border border-surface-2 bg-white px-3.5 py-2.5 text-sm text-ink-0 outline-none placeholder:text-ink-3 focus:border-[#B76E6E]"
        />
        <button
          onClick={handlePaste}
          className="flex-shrink-0 rounded-[14px] border border-surface-2 bg-[#f3efe6] px-3.5 py-2.5 text-sm font-medium text-ink-1 transition-colors hover:bg-[#ece6da]"
        >
          Dán
        </button>
      </div>

      {photo.driveLink?.trim() && (
        <p className="mt-2 truncate text-[12px] text-ink-3" title={photo.driveLink}>
          Link cũ: {photo.driveLink}
        </p>
      )}

      {hasAdminLink && (
        <div className="mt-3 rounded-[16px] border border-amber-200 bg-amber-50/80 px-3.5 py-3">
          <p className="text-[13px] font-semibold text-amber-900">
            Bộ này đã Import vào Drive của anh từ link cũ
          </p>
          <label className="mt-2 flex cursor-pointer items-start gap-2 text-[12px] text-amber-900">
            <input
              type="checkbox"
              checked={clearAdmin}
              onChange={(e) => setClearAdmin(e.target.checked)}
              className="mt-0.5 accent-[#B76E6E]"
            />
            <span>
              Xoá link admin để Import lại từ link mới. Folder copy cũ trên Drive
              phải xoá tay.
            </span>
          </label>
        </div>
      )}

      {error && <p className="mt-2 text-[12px] font-medium text-red-700">{error}</p>}

      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          onClick={onClose}
          disabled={saving}
          className="rounded-[16px] border border-surface-2 bg-white px-4 py-2.5 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-1 disabled:opacity-60"
        >
          Huỷ
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !link.trim()}
          className={cn(
            "rounded-[16px] px-4 py-2.5 text-sm font-semibold text-white transition-colors",
            "bg-[#B76E6E] hover:bg-[#a55f5f] disabled:opacity-50"
          )}
        >
          {saving ? "Đang lưu…" : "Lưu link"}
        </button>
      </div>
    </section>
  );
}
